import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toPng } from "html-to-image";
import { Download, Award, Calendar, Hash, BadgeCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import { CertificateBadge } from "./CertificateBadge";

interface CertificateProps {
  userName: string;
  score: number;
  certificateNumber: string;
  issuedAt: string;
}

export function Certificate({ userName, score, certificateNumber, issuedAt }: CertificateProps) {
  const { t } = useTranslation();
  const certificateRef = useRef<HTMLDivElement>(null);

  const downloadCertificate = async (format: 'png' | 'pdf') => {
    if (!certificateRef.current) return;

    try {
      const dataUrl = await toPng(certificateRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });

      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `calclab-latex-certificate-${certificateNumber}.png`;
        link.href = dataUrl;
        link.click();
        toast.success(t('latexCenter.certificate.downloadSuccess', 'Certificate downloaded!'));
      } else {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>CalcLab LaTeX Certificate</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                img { max-width: 100%; height: auto; }
                @media print { body { margin: 0; } }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" alt="Certificate" />
              <script>
                window.onload = function() { window.print(); }
              </script>
            </body>
            </html>
          `);
          printWindow.document.close();
        }
        toast.success(t('latexCenter.certificate.printReady', 'Print dialog opened!'));
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error(t('latexCenter.certificate.downloadError', 'Failed to download certificate'));
    }
  };

  const formattedDate = new Date(issuedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="space-y-4">
      <Tabs defaultValue="certificate" className="w-full">
        <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
          <TabsTrigger value="certificate" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('latexCenter.certificate.certificateTab', 'Certificate')}
          </TabsTrigger>
          <TabsTrigger value="badge" className="flex items-center gap-2">
            <BadgeCheck className="h-4 w-4" />
            {t('latexCenter.certificate.badgeTab', 'LinkedIn Badge')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="certificate" className="mt-6 space-y-4">
          {/* Certificate Preview */}
          <div 
            ref={certificateRef}
            className="bg-white p-8 rounded-lg shadow-lg mx-auto max-w-2xl"
            style={{ aspectRatio: '1.414/1' }}
          >
            {/* Certificate Border */}
            <div className="h-full border-4 border-double border-amber-600 p-6 relative">
              {/* Corner Decorations */}
              <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-amber-500" />
              <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-amber-500" />
              <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-amber-500" />
              <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-amber-500" />

              <div className="flex flex-col items-center justify-between h-full text-center">
                {/* Header */}
                <div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Award className="h-8 w-8 text-amber-600" />
                  </div>
                  <h1 className="text-3xl font-serif text-gray-800 tracking-wide">
                    {t('latexCenter.certificate.title', 'Certificate of Achievement')}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">CalcLab LaTeX Learning Center</p>
                </div>

                {/* Body */}
                <div className="space-y-4">
                  <p className="text-gray-600">
                    {t('latexCenter.certificate.certifies', 'This certifies that')}
                  </p>
                  <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-amber-400 pb-2 px-8">
                    {userName}
                  </h2>
                  <p className="text-gray-600 max-w-md">
                    {t('latexCenter.certificate.completed', 'has successfully completed the LaTeX Professional Report Final Project with a score of')}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl font-bold text-amber-600">{score}</span>
                    <span className="text-2xl text-gray-500">/100</span>
                  </div>
                  <p className="text-gray-600">
                    {t('latexCenter.certificate.demonstrates', 'demonstrating proficiency in LaTeX document creation')}
                  </p>
                </div>

                {/* Footer */}
                <div className="w-full flex justify-between items-end text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formattedDate}</span>
                  </div>
                  <div className="text-center">
                    <div className="w-32 border-t border-gray-400 mb-1" />
                    <span className="text-xs">CalcLab</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Hash className="h-4 w-4" />
                    <span className="font-mono text-xs">{certificateNumber}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="flex justify-center gap-3">
            <Button onClick={() => downloadCertificate('png')} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              {t('latexCenter.certificate.downloadPNG', 'Download PNG')}
            </Button>
            <Button onClick={() => downloadCertificate('pdf')}>
              <Download className="h-4 w-4 mr-2" />
              {t('latexCenter.certificate.downloadPDF', 'Print/Save as PDF')}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="badge" className="mt-6">
          <CertificateBadge
            userName={userName}
            certificateNumber={certificateNumber}
            issuedAt={issuedAt}
            canDownload={true}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
